/* Gigli — BOGOS discount price preview (assets/gigli-bogos-price.js).

   What BOGOS actually does here, verified against the live store: its "product" discounts are real
   Shopify discounts, but they carry an auto-generated CODE (BOGOS-xxxxxx) rather than being purely
   automatic, and BOGOS's own script applies that code to the cart. That is why the saving only
   surfaces after an add to cart — and why pricing a cart WITHOUT the code shows nothing at all.

   So this does two things:

     1. Reads window.fgData.discounts, which BOGOS loads on every page, for the codes of its
        currently-active `product` discounts and the variants each one targets. BOGOS is used only
        to discover WHICH code to try and whether this product is worth asking about — never for
        the price.
     2. Prices a THROWAWAY cart through the Storefront API with that code applied, and takes the
        per-line discountAllocations as the truth. Shopify stays the source of the number, so a
        stale or changed BOGOS rule can cost us a preview but can never show a wrong price.

   One cartCreate per applicable code per product, cached in sessionStorage for the session. A
   product no discount targets costs no request at all, and renders exactly as it did before.

   Volume discounts (BOGOS type "volume", e.g. "4 for $20") are deliberately skipped: they need a
   quantity threshold this probe does not buy, so previewing them would misprice a single unit. */
(function () {
  const el = document.getElementById("gigli-bogos-price-config");
  if (!el) return;

  let CFG;
  try {
    CFG = JSON.parse(el.textContent);
  } catch (e) {
    /* Loud on purpose. A malformed config disables the whole preview for that product, and the
       silence is what let a leading comma in the variant list hide on out-of-stock-first products. */
    console.warn("[gigli-bogos-price] config is not valid JSON; price preview disabled.", e);
    return;
  }
  if (!CFG || !CFG.token || !CFG.variants || !CFG.variants.length) return;

  const CACHE_KEY =
    "gigli:bogos:" +
    CFG.productId +
    ":" +
    CFG.variants.map((v) => v.id).join(",");
  const CACHE_TTL = 10 * 60 * 1000;
  const OURS = CFG.variants.reduce((m, v) => ((m[String(v.id)] = true), m), {});

  /* BOGOS timestamps carry microseconds, which not every engine parses; trim to milliseconds. */
  function when(t) {
    return t ? Date.parse(String(t).replace(/(\.\d{3})\d+/, "$1")) : NaN;
  }

  /* The codes worth trying for THIS product: active, product-scoped, and targeting a variant that
     is actually on this page. Falls back to the manual setting when BOGOS has not loaded or has
     changed shape, so the feature degrades to "configured codes" rather than to a wrong price. */
  function codesFor() {
    const manual = (CFG.codes || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const fg = window.fgData;
    if (!fg || !Array.isArray(fg.discounts)) return manual;
    const now = Date.now();
    const found = fg.discounts
      .filter(function (d) {
        if (!d || !d.code || d.type !== "product") return false;
        const from = when(d.start_time),
          to = when(d.end_time);
        if (!isNaN(from) && now < from) return false;
        if (!isNaN(to) && now > to) return false;
        return targets(d);
      })
      .map((d) => d.code);
    return found.length ? found : manual;
  }

  /* Does this discount name any variant on this page? BOGOS stores the rule as
     condition.products[].value[productId].variants[]. Unknown shape -> assume yes and let the
     probe decide, rather than silently skipping a real offer. */
  function targets(d) {
    const groups = d.condition && d.condition.products;
    if (!Array.isArray(groups) || !groups.length) return true;
    let sawShape = false;
    for (const g of groups) {
      const byProduct = g && g.value;
      if (!byProduct || typeof byProduct !== "object") continue;
      for (const key of Object.keys(byProduct)) {
        const entry = byProduct[key];
        if (!entry || !Array.isArray(entry.variants)) continue;
        sawShape = true;
        if (entry.variants.some((v) => OURS[String(v && v.id)])) return true;
      }
    }
    return !sawShape;
  }

  /* BOGOS sets window.fgData from its own bundle, so it may not be there yet on first paint. */
  function whenReady() {
    return new Promise(function (resolve) {
      if (window.fgData && Array.isArray(window.fgData.discounts))
        return resolve();
      let waited = 0;
      const t = setInterval(function () {
        waited += 250;
        if (
          (window.fgData && Array.isArray(window.fgData.discounts)) ||
          waited >= 8000
        ) {
          clearInterval(t);
          resolve();
        }
      }, 250);
    });
  }

  /* ---------- money ---------- */
  function formatMoney(cents) {
    const fmt = CFG.moneyFormat || "${{amount}}";
    const v = Math.max(0, Math.round(cents));
    function part(precision, thousands, decimal) {
      const n = (v / 100).toFixed(precision);
      const bits = n.split(".");
      bits[0] = bits[0].replace(/(\d)(?=(\d\d\d)+$)/g, "$1" + thousands);
      return bits.length > 1 ? bits[0] + decimal + bits[1] : bits[0];
    }
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, name) {
      switch (name) {
        case "amount":
          return part(2, ",", ".");
        case "amount_no_decimals":
          return part(0, ",", ".");
        case "amount_with_comma_separator":
          return part(2, ".", ",");
        case "amount_no_decimals_with_comma_separator":
          return part(0, ".", ",");
        case "amount_with_apostrophe_separator":
          return part(2, "'", ".");
        case "amount_with_space_separator":
          return part(2, " ", ",");
        case "amount_no_decimals_with_space_separator":
          return part(0, " ", ",");
        default:
          return part(2, ",", ".");
      }
    });
  }

  /* ---------- the probe ---------- */
  const QUERY = [
    "mutation gigliBogosProbe($lines:[CartLineInput!]!,$codes:[String!]){",
    "  cartCreate(input:{lines:$lines,discountCodes:$codes}){",
    "    cart{ lines(first:100){ nodes{",
    "      quantity",
    "      merchandise{ ... on ProductVariant { id } }",
    "      cost{ amountPerQuantity{amount} subtotalAmount{amount} totalAmount{amount} }",
    "      discountAllocations{ discountedAmount{amount} }",
    "    } } }",
    "    userErrors{ field message }",
    "  }",
    "}",
  ].join("\n");

  function probe(code) {
    const lines = CFG.variants.map((v) => ({
      merchandiseId: v.gid,
      quantity: CFG.probeQuantity || 1,
    }));
    return fetch(
      "https://" + CFG.domain + "/api/" + CFG.apiVersion + "/graphql.json",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": CFG.token,
        },
        body: JSON.stringify({
          query: QUERY,
          variables: { lines: lines, codes: [code] },
        }),
      }
    )
      .then((r) =>
        r.ok ? r.json() : Promise.reject(new Error("storefront " + r.status))
      )
      .then(function (json) {
        const create = json && json.data && json.data.cartCreate;
        if (!create || !create.cart) throw new Error("cartCreate failed");
        const out = {};
        create.cart.lines.nodes.forEach(function (line) {
          const gid = line.merchandise && line.merchandise.id;
          if (!gid) return;
          const qty = line.quantity || 1;
          const unit = Math.round(
            parseFloat(line.cost.amountPerQuantity.amount) * 100
          );
          /* totalAmount is the line AFTER line-level discounts; fall back to subtracting the
             allocations when a currency or API quirk leaves it unset. */
          let discounted = unit;
          if (line.cost.totalAmount && line.cost.totalAmount.amount != null) {
            discounted = Math.round(
              (parseFloat(line.cost.totalAmount.amount) * 100) / qty
            );
          } else {
            const off = (line.discountAllocations || []).reduce(
              (s, a) => s + parseFloat(a.discountedAmount.amount) * 100,
              0
            );
            discounted = unit - Math.round(off / qty);
          }
          if (discounted < unit) out[gid] = { was: unit, now: discounted };
        });
        return out;
      });
  }

  function cached() {
    try {
      const raw = sessionStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const o = JSON.parse(raw);
      if (!o || Date.now() - o.t > CACHE_TTL) return null;
      return o.d;
    } catch (e) {
      return null;
    }
  }
  function store(d) {
    try {
      sessionStorage.setItem(
        CACHE_KEY,
        JSON.stringify({ t: Date.now(), d: d })
      );
    } catch (e) {}
  }

  /* ---------- rendering ---------- */
  function apply(found) {
    const byGid = CFG.variants.reduce((m, v) => ((m[v.gid] = v), m), {});
    const hits = Object.keys(found)
      .map((gid) => Object.assign({}, byGid[gid], found[gid]))
      .filter((h) => h.id);
    if (!hits.length) return;

    /* product-meta shows a "from" price across variants, so the one worth naming is whichever
       discounted variant ends up cheapest. */
    hits.sort((a, b) => a.now - b.now);
    const best = hits[0];

    document
      .querySelectorAll(".product-meta price-list sale-price")
      .forEach(function (priceEl) {
        const meta = priceEl.closest(".product-meta");
        if (!meta || meta.querySelector(".product-meta__discount")) return;
        priceEl.classList.add("product-meta__price--was");
        const span = document.createElement("span");
        span.className = "product-meta__discount";
        span.textContent =
          " " +
          CFG.connector +
          " " +
          formatMoney(best.now) +
          (best.value
            ? " " +
              CFG.onWord +
              " " +
              best.value +
              (CFG.optionName ? " " + CFG.optionName : "")
            : "");
        priceEl.parentNode.insertBefore(span, priceEl.nextSibling);
      });

    wireRecharge(found);
  }

  /* The Recharge purchase options live inside recharge-subscription-widget's SHADOW root, not the
     light DOM — document.querySelector can never reach them, and the plain-descendant rules for
     .rc-selection__onetime in recharge-subscriptions.min.css never match either (the ::part rules
     beside them are what actually style it). So this pierces the shadow root, and styles it by
     adopting a stylesheet into that root the way assets/gigli-recharge-cart.js already does for the
     cart drawer.

     Unlike product-meta's "from" price, this only applies when the variant actually SELECTED is the
     discounted one. The widget is a lit component that re-renders on every variant change and
     throws our node away with it, so the observer re-applies rather than setting it once. */
  const RC_CSS =
    ".rc-purchase-option__prices{flex-direction:row !important;}" +
    ".rc-price--was{text-decoration:line-through;opacity:.55}" +
    ".rc-price__discount{font-weight:700;margin-inline-start:6px;font-family: 'Oakes Grotesk';}";

  function rcRoot() {
    const host = document.querySelector("recharge-subscription-widget");
    return host && host.shadowRoot ? host.shadowRoot : null;
  }

  function injectRcStyles(sr) {
    try {
      if (sr.adoptedStyleSheets && typeof CSSStyleSheet === "function") {
        if (sr.adoptedStyleSheets.some((x) => x.__gigliBogos)) return;
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(RC_CSS);
        sheet.__gigliBogos = true;
        sr.adoptedStyleSheets = sr.adoptedStyleSheets.concat(sheet);
        return;
      }
    } catch (e) {}
    if (sr.querySelector("style[data-gigli-bogos]")) return;
    const tag = document.createElement("style");
    tag.setAttribute("data-gigli-bogos", "");
    tag.textContent = RC_CSS;
    sr.appendChild(tag);
  }

  function wireRecharge(found) {
    const form = document.querySelector('input[name="id"]');
    let frame = null,
      observed = null;

    function paint() {
      frame = null;
      const sr = rcRoot();
      if (!sr) return;
      injectRcStyles(sr);
      const price = sr.querySelector(
        ".rc-selection__onetime .rc-price, .rc-purchase-option__onetime .rc-price"
      );
      if (!price) return;

      const id = form && form.value;
      const hit = id && found["gid://shopify/ProductVariant/" + id];
      const prices = price.parentNode;
      const existing = prices.querySelector(".rc-price__discount");

      if (!hit) {
        price.classList.remove("rc-price--was");
        if (existing) existing.remove();
        return;
      }
      price.classList.add("rc-price--was");
      const text = " " + formatMoney(hit.now);
      if (existing) {
        if (existing.textContent !== text) existing.textContent = text;
        return;
      }
      const span = document.createElement("span");
      span.className = "rc-price__discount";
      span.setAttribute("part", "gigli-discounted-price");
      span.textContent = text;
      prices.appendChild(span);
    }
    function schedule() {
      if (frame === null) frame = requestAnimationFrame(paint);
    }

    /* The widget mounts lazily, so watch for the host before watching inside it. */
    function attach() {
      const sr = rcRoot();
      if (!sr || observed === sr) return;
      observed = sr;
      new MutationObserver(schedule).observe(sr, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      schedule();
    }
    attach();
    new MutationObserver(attach).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
    document.addEventListener("change", schedule);
  }

  const hit = cached();
  if (hit) {
    apply(hit);
    return;
  }

  whenReady()
    .then(function () {
      const codes = codesFor();
      if (!codes.length) {
        store({});
        return;
      }
      /* one cart per code: two product-scoped discounts can target different variants, and Shopify
       will only resolve one product discount per cart. */
      return Promise.all(codes.map((c) => probe(c).catch(() => ({})))).then(
        function (results) {
          const merged = {};
          results.forEach(function (r) {
            Object.keys(r).forEach(function (gid) {
              if (!merged[gid] || r[gid].now < merged[gid].now)
                merged[gid] = r[gid];
            });
          });
          store(merged);
          apply(merged);
        }
      );
    })
    .catch(function () {
      /* leave the page as it was */
    });
})();
